import { defineStore } from 'pinia';
import { getCompanies, getCompaniesByLetter, fetchCompanyById, createCompany, updateCompany, deleteCompany } from '@/services/companyService';

export const useCompanyStore = defineStore('companies', {
    state: () => ({
        companies: [],
        loading: false,
        error: null,
        pagination: {
            current_page: 1,
            total: 0,
            per_page: 50
        }
    }),
    actions: {
        async loadCompanies(page = 1, perPage = 50) {
            this.loading = true;
            this.error = null;
            try {
                const response = await getCompanies(page, perPage);
                console.log('API Response:', response);
                
                // Handle different possible response structures
                if (Array.isArray(response)) {
                    // Direct array response
                    this.companies = response;
                } else if (response.data && Array.isArray(response.data)) {
                    // Response with data array
                    this.companies = response.data;
                    if (response.current_page) {
                        this.pagination = {
                            current_page: response.current_page,
                            total: response.total || response.data.length,
                            per_page: response.per_page || perPage
                        };
                    }
                } else if (response.companies && Array.isArray(response.companies)) {
                    // Response with companies property
                    this.companies = response.companies;
                    if (response.pagination) {
                        this.pagination = response.pagination;
                    }
                } else {
                    // Fallback - try to extract any array from the response
                    const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
                    if (possibleArrays.length > 0) {
                        this.companies = possibleArrays[0];
                    } else {
                        this.companies = [];
                        console.warn('No companies array found in response:', response);
                    }
                }
                
                console.log('Processed companies:', this.companies);
            } catch (error) {
                console.error('Error loading companies:', error);
                this.error = error.message;
                this.companies = [];
            } finally {
                this.loading = false;
            }
        },
        async loadCompaniesByLetter(letter = null) {
            this.loading = true;
            this.error = null;
            try {
                const response = await getCompaniesByLetter(letter);
                console.log('API Response by letter:', response);
                
                // Handle different possible response structures
                if (Array.isArray(response)) {
                    // Direct array response
                    this.companies = response;
                } else if (response.data && Array.isArray(response.data)) {
                    // Response with data array
                    this.companies = response.data;
                } else if (response.companies && Array.isArray(response.companies)) {
                    // Response with companies property
                    this.companies = response.companies;
                } else {
                    // Fallback - try to extract any array from the response
                    const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
                    if (possibleArrays.length > 0) {
                        this.companies = possibleArrays[0];
                    } else {
                        this.companies = [];
                        console.warn('No companies array found in response:', response);
                    }
                }
                
                console.log('Processed companies by letter:', this.companies);
            } catch (error) {
                console.error('Error loading companies by letter:', error);
                this.error = error.message;
                this.companies = [];
            } finally {
                this.loading = false;
            }
        },
        async getCompanyById(id) {
            try {
                const response = await fetchCompanyById(id);
                // Backend returns: { success: true, data: {...} }
                const company = response.data || response;
                return company;
            } catch (error) {
                console.error('Error fetching company by ID:', error);
                throw error;
            }
        },
        async updateCompany(updatedCompany) {
            try {
                const response = await updateCompany(updatedCompany.id, updatedCompany);
                // Backend returns: { success: true, message: "...", data: {...} }
                const company = response.data || response;
                // Update in local state if found
                const index = this.companies.findIndex((c) => c.id === company.id);
                if (index !== -1) {
                    this.companies[index] = company;
                }
                return company;
            } catch (error) {
                console.error('Error updating company:', error);
                throw error;
            }
        },
        async deleteCompany(id) {
            try {
                await deleteCompany(id);
                this.companies = this.companies.filter((c) => c.id !== id);
            } catch (error) {
                console.error('Error deleting company:', error);
                throw error;
            }
        },
        async createCompany(companyData) {
            try {
                const response = await createCompany(companyData);
                // Backend returns: { success: true, message: "...", data: {...} }
                const company = response.data || response;
                this.companies.push(company);
                return company;
            } catch (error) {
                console.error('Error creating company:', error);
                throw error;
            }
        }
    },
});
